import moment from 'moment-timezone';

// Portal display standard (owner): dates MM/DD/YYYY, date-times MM/DD/YYYY HH:mm:ss (24-hour) in Eastern Time.
// Display only: stored values and API payloads are never changed here.
export const TZ = 'America/New_York';
export const DATE_FORMAT = 'MM/DD/YYYY';
export const TIME_FORMAT = 'HH:mm:ss';
export const DATE_TIME_FORMAT = `${DATE_FORMAT} ${TIME_FORMAT}`;
export const DATE_PLACEHOLDER = 'MM/DD/YYYY';

const YMD = /^(\d{4})[-/](\d{2})[-/](\d{2})$/;
const US_DATE = /^(\d{2})\/(\d{2})\/(\d{4})$/;
const CLOCK = /^(\d{1,2})[:.](\d{2})(?::(\d{2}))?\s*([AaPp][Mm])?$/;

const blank = (v) => v === null || v === undefined || v === '';

/** Instant (Date, ISO string, epoch ms, moment) -> moment in ET, or null. */
const toEt = (v) => {
    if (blank(v)) return null;
    let m;
    if (moment.isMoment(v)) m = v.clone();
    else if (v instanceof Date || typeof v === 'number') m = moment(v);
    else if (typeof v === 'string' && /^\d{10,}$/.test(v.trim())) m = moment(Number(v.trim()));
    else if (typeof v === 'string') m = moment(v.trim(), moment.ISO_8601, true);
    else return null;
    return m.isValid() ? m.tz(TZ) : null;
};

const ymdParts = (v) => {
    const m = typeof v === 'string' ? YMD.exec(v.trim()) : null;
    if (!m) return null;
    const c = moment.utc(`${m[1]}-${m[2]}-${m[3]}`, 'YYYY-MM-DD', true);
    return c.isValid() ? c : null;
};

/** Calendar-day value (e.g. 'YYYY-MM-DD', 'YYYY/MM/DD', or a Date at UTC midnight) -> 'MM/DD/YYYY' with no timezone shift. */
export const formatCalendarDate = (v, fallback = '') => {
    if (blank(v)) return fallback;
    const ymd = ymdParts(v);
    if (ymd) return ymd.format(DATE_FORMAT);
    if (typeof v === 'string' && US_DATE.test(v.trim())) return v.trim();
    const m = moment.isMoment(v) ? v.clone().utc() : moment.utc(v instanceof Date || typeof v === 'number' ? v : moment.utc(String(v).trim(), moment.ISO_8601, true));
    return m.isValid() ? m.format(DATE_FORMAT) : fallback;
};

/** Date of birth: a calendar date, never shifted by timezone. */
export const formatDob = (v, fallback = '') => formatCalendarDate(v, fallback);

/** Whole years of age from a calendar-date DOB (UTC calendar day, never timezone-shifted) as of today's ET date. */
export const ageFromDob = (v, now = new Date(), fallback = '') => {
    const d = formatCalendarDate(v);
    if (!d) return fallback;
    const [mm, dd, yyyy] = d.split('/').map(Number);
    const today = moment(now).tz(TZ);
    const m = today.month() + 1;
    return today.year() - yyyy - (m < mm || (m === mm && today.date() < dd) ? 1 : 0);
};

/** Date shown in ET. Plain calendar strings ('YYYY-MM-DD') are shown as-is (no timezone conversion). */
export const formatDate = (v, fallback = '') => {
    if (blank(v)) return fallback;
    if (typeof v === 'string' && (ymdParts(v) || US_DATE.test(v.trim()))) return formatCalendarDate(v, fallback);
    const m = toEt(v);
    return m ? m.format(DATE_FORMAT) : fallback;
};

/** 'MM/DD/YYYY HH:mm:ss ET' (pass { et: false } to drop the suffix where space is tight). */
export const formatDateTime = (v, { et = true, fallback = '' } = {}) => {
    const m = toEt(v);
    return m ? `${m.format(DATE_TIME_FORMAT)}${et ? ' ET' : ''}` : fallback;
};

/**
 * 24-hour time. Instants -> 'HH:mm:ss' in ET. Wall-clock strings already in ET ('14:05', '2:05 PM') -> 'HH:mm' (no conversion).
 */
export const formatTime = (v, { et = false, fallback = '' } = {}) => {
    if (blank(v)) return fallback;
    const c = typeof v === 'string' ? CLOCK.exec(v.trim()) : null;
    if (c) {
        let h = Number(c[1]);
        const mer = c[4] && c[4].toUpperCase();
        if (mer === 'PM' && h < 12) h += 12;
        if (mer === 'AM' && h === 12) h = 0;
        if (h > 23 || Number(c[2]) > 59) return fallback;
        const s = `${String(h).padStart(2, '0')}:${c[2]}${c[3] ? `:${c[3]}` : ''}`;
        return `${s}${et ? ' ET' : ''}`;
    }
    const m = toEt(v);
    return m ? `${m.format(TIME_FORMAT)}${et ? ' ET' : ''}` : fallback;
};

/** Month-only label, e.g. 'November 2026'. */
export const formatMonth = (v, fallback = '') => {
    if (blank(v)) return fallback;
    const ym = typeof v === 'string' && /^\d{4}-\d{2}$/.test(v.trim()) ? moment(v.trim(), 'YYYY-MM', true) : null;
    if (ym) return ym.isValid() ? ym.format('MMMM YYYY') : fallback;
    const m = toEt(v);
    return m ? m.format('MMMM YYYY') : fallback;
};

/**
 * Device "assignedTime" is stored as 'DD/MM/YYYY' (ET calendar day of the assignment); a few legacy rows hold epoch ms.
 * Returns MM/DD/YYYY.
 */
export const parseDeviceAssignedDate = (v) => {
    if (blank(v)) return null;
    if (typeof v === 'number' || (typeof v === 'string' && /^\d{10,}$/.test(v.trim()))) return toEt(v);
    const d = typeof v === 'string' && /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(v.trim());
    if (d) {
        const m = moment.tz(`${d[3]}-${d[2]}-${d[1]}`, 'YYYY-MM-DD', true, TZ);
        return m.isValid() ? m : null;
    }
    return toEt(v);
};
export const formatDeviceAssignedDate = (v, fallback = '') => {
    const m = parseDeviceAssignedDate(v);
    return m ? m.format(DATE_FORMAT) : fallback;
};
